import React, { useContext } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { BotType, ExchangeName, LanguageNameSpaces } from "@shared/types";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import useGroupMainCoinsSignalsCount from "@shared/features/bots/BotForm/useGroupMainCoinsSignalsCount";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useSelectUser } from "@shared/utils/hooks/misc";
import { isSymbol } from "@shared/types/utils";
import { MARKETPLACE_PATH } from "common/constants";
import Text from "common/Typography/Text";
import Link from "common/Typography/Link";
import {
  BOT_FORM_NO_SIGNALS_SUGGESTION_ID,
  BOT_FORM_BROWSE_GROUPS_ID,
} from "testIds";

import RecommendedMarketplaceGroups from "features/bots/BotForm/common/RecommendedMarketplaceGroups";

interface NoGroupSignalsSuggestionProps {
  exchange?: ExchangeName;
}

const NoGroupSignalsSuggestion = ({
  exchange,
}: NoGroupSignalsSuggestionProps): React.JSX.Element | null => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const showSuggestion = useShowSuggestion(exchange);
  if (!showSuggestion) return null;
  return (
    <Suggestion role="status" data-testid={BOT_FORM_NO_SIGNALS_SUGGESTION_ID}>
      <Text>
        {t("empty_states.no_signals_in_selected_exchange_last_30_days")}
      </Text>
      <Text type="secondary">{t("no_signals_suggestion.description")}</Text>
      <RecommendedMarketplaceGroups />
      <BrowseGroupsButton />
    </Suggestion>
  );
};

export default NoGroupSignalsSuggestion;

const useShowSuggestion = (exchange?: ExchangeName): boolean => {
  const { account } = useContext(BotFormContext)!;
  const isEligible = useIsSuggestionEligible();
  const { mainCoinsToSignalsCount, hasExactSignalsCount, isFetching, isError } =
    useGroupMainCoinsSignalsCount({});
  const selectedExchange =
    exchange ?? (isSymbol(account) ? undefined : account?.exchange);
  if (!isEligible || !selectedExchange) return false;
  if (
    !hasExactSignalsCount ||
    isFetching ||
    isError ||
    !mainCoinsToSignalsCount
  )
    return false;
  return !Object.values(mainCoinsToSignalsCount[selectedExchange] ?? {}).some(
    (count) => count > 0
  );
};

const useIsSuggestionEligible = (): boolean => {
  const { botType, botFormType, subGroup, isChannelConfiguration } =
    useContext(BotFormContext)!;
  const user = useSelectUser();
  return (
    botType === BotType.SIGNAL &&
    !!subGroup &&
    !isChannelConfiguration &&
    ![BotFormType.EDIT, BotFormType.BULK_EDIT].includes(botFormType) &&
    !user?.is_sub_user &&
    !user?.isWhopExperience()
  );
};

const BrowseGroupsButton = (): React.JSX.Element => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const browseGroups = useBrowseGroups();
  return (
    <Link
      as="button"
      type="button"
      data-testid={BOT_FORM_BROWSE_GROUPS_ID}
      onClick={browseGroups}
      style={{ background: "none", border: 0, padding: 0, textAlign: "start" }}
    >
      {t("no_signals_suggestion.browse_groups")}
    </Link>
  );
};

const useBrowseGroups = (): (() => void) => {
  const onboarding = useContext(OnboardingContext);
  const history = useHistory();
  return () => {
    if (onboarding)
      onboarding.changeStep(OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP, {
        channelTab: "trial",
      });
    else history.push(MARKETPLACE_PATH);
  };
};

const Suggestion = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 16px;
  border: ${(props) => props.theme.borders.lightest};
  border-radius: ${(props) => props.theme.radii.control}px;
  background: ${(props) => props.theme.colors.cardLightBgColor};
`;
